import Vue from 'vue'
import App from './App.vue'
import Api from './classes/Api.class'
import router from './router'
import { VueReCaptcha } from 'vue-recaptcha-v3'

import Env from './classes/utils/Env'

import * as Sentry from '@sentry/browser';
import * as Integrations from '@sentry/integrations';

import LogRocket from 'logrocket'

if (!Env.isDevelopment()) {
  Sentry.init({
    dsn: 'https://c59823087a2e4c669e0b47c460405308@sentry.io/5176610',
    integrations: [new Integrations.Vue({Vue, attachProps: true})],
  })
}

import moment from 'moment'
import 'moment-timezone'

import UI from 'fanplayr-ui'
Vue.use(UI, { customTranslate: true })

import { ValidationProvider, ValidationObserver, extend } from 'vee-validate/dist/vee-validate.full'
import { required, email } from 'vee-validate/dist/rules';
Vue.component('ValidationProvider', ValidationProvider)
Vue.component('ValidationObserver', ValidationObserver)

import LoadingLayout from './components/LoadingLayout.vue'
Vue.component('LoadingLayout', LoadingLayout)

import VueShortKey from 'vue-shortkey'
Vue.use(VueShortKey)

import UnauthenticatedError from '@/classes/errors/UnauthenticatedError'
import ForbiddenError from '@/classes/errors/ForbiddenError'
import RequiredError from '@/classes/errors/RequiredError'
import InvalidError from '@/classes/errors/InvalidError'
import UserError from '@/classes/errors/UserError'

import md5 from 'md5'

import PortalI18n from 'PortalI18n'

PortalI18n.createExtendedLocale('en-debug1', 'en', key => {
  return `❗"${key}"`
})
PortalI18n.createExtendedLocale('en-debug2', 'en', key => {
  return `⋮${key}⋮`
})

Vue.use(PortalI18n, {
  defaultLocale: localStorage.getItem('portal-language') || 'en'
})

extend('required', {
  ...required,
  message: (_, values) => PortalI18n.translate('validation.required', values)
})

extend('email', {
  ...email,
  message: (_, values) => PortalI18n.translate('validation.email', values)
})

const promisify = require("promisify-node")
const jwt = promisify(require('jsonwebtoken'))

Vue.config.productionTip = false

Vue.use(VueReCaptcha, { siteKey: '6LfaBL0UAAAAAAzCKA2vuxd_ZxjRpMCCcb8o4Mb7' })

Vue.component(ValidationProvider)

window.moment = moment
window.Vue = Vue

const eventHub = new Vue()

// used in getter/setter so only one
let localData = new Vue({
  data() {
    return {
      agencyAccounts: null,
      theUser: {
        name: '',
        logoUrl: ''
      },
      dialogues: null,
      appMountComplete: null,
    }
  }
})

// only used here
let loginRedirect = null
let refreshInterval

function jwtToUserData(d) {
  return {
    userId: d.uid,
    accountId: d.aid,
    onBehalfOfAccountId: d.obo,
    accountTimezone: d.atz,
    isSuper: d.isp === 1,
    isAgency: d.iag === 1,
    isAdmin: d.iad === 1,
    isDemo: d.ide === 1,
    isSmartEnabled: d.ist === 1,
    hasCampaignAccess: d.acc === 1,
    hasInvoiceAccess: d.aci === 1,
    hasTransactionAccess: d.act === 1,
    hasCreativeAccess: d.acw === 1,
    hasConversionDefinitionAccess: d.acd === 1,
    hasNotificationAccess: d.ant === 1,
    webPushEnabled: d.ewp === 1,
    twoFactorAuthentication: d.fa2
  }
}

async function processPostLogin(loginResponse) {

  if (!loginResponse) {
    return false
  }

  let { token, sessionCookieSeconds } =  loginResponse

  // must be session ID
  if (token.length === 64) {
    Api.get().setToken(token)
    let data = await Api.get().getBaseUserDetails()
    localData.theUser = data
    if (!localData.theUser.onBehalfOfAccountId) {
      localData.theUser.onBehalfOfAccountId = localData.theUser.accountId
    }
    localData.theUser.userId = localData.theUser.id
  } else {
    Api.get().setToken(md5(token))
    localData.theUser = jwtToUserData(jwt.decode(token))
  }

  restartRefreshCounter(sessionCookieSeconds)

  return true
}

async function doLogin(redirectTo = null, message = '') {

  if (redirectTo) {
    loginRedirect = redirectTo
  }

  await processPostLogin(await localData.dialogues.login(message))
  await loadExtraDetails()

  if (loginRedirect) {
    router.push(loginRedirect)
    loginRedirect = null
  }
}

async function loadExtraDetails() {
  // load user / accoutn details that are not in token
  let extraDetails = await Api.get().getExtraUserDetails()
  localData.theUser.email = extraDetails.user.email
  localData.theUser.firstName = extraDetails.user.firstName
  localData.theUser.lastName = extraDetails.user.lastName
  localData.theUser.defaultLanguage = window.localStorage['portal-language'] || extraDetails.user.defaultLanguage || 'en'
  localData.theUser.accountName = extraDetails.account.name
  localData.theUser.logoUrl = extraDetails.account.logoURL
  localData.theUser.accountCurrency = extraDetails.account.currency
  localData.theUser.serverVersion = extraDetails.version

  localData.agencyAccounts = await Api.get().getAgencyAccounts()

  if (!Env.isDevelopment()) {
    LogRocket.init('qe1mz0/portal-new', {
      release: require('./version.json').version
    })
    LogRocket.identify(localData.theUser.userId, {
      name: `${localData.theUser.firstName} ${localData.theUser.lastName}`,
      email: localData.theUser.email
    })
  }

  await setLanguage(localData.theUser.defaultLanguage)
}

async function doRefreshToken() {
  const loginResponse = await Api.get().refreshToken()
  await processPostLogin(loginResponse)
  await loadExtraDetails()
  eventHub.$emit('tokenRefreshed')
}

function restartRefreshCounter(sessionCookieSeconds = 60) {
  clearInterval(refreshInterval)
  refreshInterval = setInterval(() => {
    doRefreshToken()
  }, sessionCookieSeconds * 1000 - 30 * 1000)
}

async function setLanguage(lang) {
  return PortalI18n.setLocale(lang)
}

async function setCustomLocale(lang, dictionary) {
  return PortalI18n.setCustomLocale(lang, dictionary)
}

// send to sentry.io
function sentryError(error) {
  if (localData.theUser) {
    Sentry.setUser({
      'email': `${localData.theUser.email}`,
      'accountId': localData.theUser.accountId,
      'onBehalfOfAccountId': localData.theUser.onBehalfOfAccountId
    })
  }
  Sentry.captureException(error)
}

Vue.config.errorHandler = (error) => {
  console.log('Vue->config->errorHandler')
  
  // if we're doing a load may as well stop it
  NProgress.done()
  if (error instanceof UnauthenticatedError) {
    console.warn(error)
    doLogin()
  } else if (error instanceof ForbiddenError ) {
    UI.alert({ title: PortalI18n.translate('Unauthorized to Access') })
  } else {
    sentryError(error)
    console.error(error)
    UI.alert({ title: error.message ? error.message : error })
  }
}

Vue.mixin({
  data() {
    return {
      get api() {
        return Api.get()
      },
      get eventHub() {
        return eventHub
      }
    }
  },
  mounted() {
  },
  computed: {
    theUser: {
      get() {
        return localData.theUser
      },
      set(newVal) {
        localData.theUser = newVal
      }
    },
    agencyAccounts: {
      get() {
        if (!localData.agencyAccounts) {
          return null
        }
        return JSON.parse(JSON.stringify(localData.agencyAccounts)).sort((a, b) => {
          if (a.name.toLowerCase() > b.name.toLowerCase()) return 1
          if (a.name.toLowerCase() < b.name.toLowerCase()) return -1
          return 0
        })
      },
      set(newVal) {
        localData.agencyAccounts = newVal
      }
    },
    $dialogue: {
      get() {
        return localData.dialogues
      },
      set(newVal) {
        localData.dialogues = newVal
      }
    },
    appMountComplete: {
      get() {
        return localData.appMountComplete
      },
      set(newVal) {
        localData.appMountComplete = newVal
      }
    },
    supportedLanguages() {
      return PortalI18n.getAvailableLocales().map(locale => ({
        value: locale,
        label: this.$t(`language.${locale}`)
      })).sort((a, b) => {
        if (a.label > b.label) return 1
        if (a.label < b.label) return -1
        return 0
      }).filter((lang) => {
        if (Env.isProduction()) {
          return ['en', 'it', 'es', 'ja'].includes(lang.value)
        } else {
          return true
        }
      })
    },
    langCountryMap() {
      return {
        en: [
          'US',
          'GB',
          'AU',
          'CA',
          'NZ'
        ],
        es: [
          'MX',
          'ES',
          'AR',
          'BO',
          'CL',
          'CO',
          'CR',
          'CU',
          'DO',
          'EC',
          'SV',
          'GQ',
          'GT',
          'HN',
          'NI',
          'PA',
          'PY',
          'PE',
          'PR',
          'UY',
          'VE'
        ],
        pt: [
          'PT',
          'BR'
        ],
      }
    }
  },
  methods: {
    setLanguage,
    setCustomLocale,
    formError(error, output) {
      const rTrue = () => {
        this.$forceUpdate()
        return true
      }
      if (error instanceof RequiredError) {
        for (const field of error.fields) {
          output[field] = 'Required'
        }
        return rTrue()
      } else if (error instanceof InvalidError) {
        for (const { key, error } of error.errors) {
          output[key] = error
        }
        return rTrue()
      } else if (!(error instanceof UnauthenticatedError || error instanceof ForbiddenError)) {
        output['other'] = error.message
        return rTrue()
      }
      return false
    },
    apiCatch(error) {
      if (error instanceof UnauthenticatedError) {
        doLogin(null, 'unauthenticated')
      } else if (error instanceof ForbiddenError ) {
        UI.alert({ title: PortalI18n.translate('Unauthorized to Access') })
      } else {
        sentryError(error)
        console.error(error)
        if (error instanceof UserError) {
          UI.alert({ title: PortalI18n.translate('Error'), content: PortalI18n.translate(error.message) })
        }
      }
    },
    duplicateRouteCatch(e) {
      if (!e) return
      if (e.name !== 'NavigationDuplicated') throw e
    },
    async doLogin(redirectTo, message = '') {
      return doLogin(redirectTo, message)
    },
    async doRefreshToken() {
      return doRefreshToken()
    },
    async processPostLogin(loginResponse) {
      return processPostLogin(loginResponse)
    },
    async loadExtraDetails() {
      return loadExtraDetails()
    },
    setTableLimit(key, limit) {
      window.localStorage[key] = limit
      return true
    },
    getTableLimit(key) {
      return window.localStorage[key] && parseInt(window.localStorage[key])
    },
    getFlag(value) {
      if (window.localStorage['portal-flag']) {
        const flagChange = JSON.parse(window.localStorage['portal-flag'])
        if (flagChange[value]){
          return flagChange[value] 
        } 
      }
      return value ? value.toUpperCase() : ''
    },
    outsideValidityPeriod(offer) {
      const {validFromDate, expiryDate} = offer
      const timezone = this.theUser.accountTimezone
      const now = moment.utc().tz(timezone)
      return (
        (validFromDate && moment.tz(validFromDate, timezone).isAfter(now)) ||
        (expiryDate && moment.tz(expiryDate, timezone).isBefore(now))
      )
    }
  }
})

router.afterEach(() => {
  if (window.localStorage['portal-version-reload'] === 'TRUE') {
    window.localStorage['portal-version-reload'] = ''
    setTimeout(() => {
      window.location.reload(true)
    }, 1)
  }
  window.scrollTo({
    top: 0,
    left: 0,
    behavior: 'smooth'
  })
})

function allowedNoSmartPath(path) {
  const allowed = [
    '/notEnabled/',
    '/site/',
    '/account/',
    '/logout/'
  ]
  for (const p of allowed) {
    if (path.indexOf(p) === 0) {
      return true
    }
  }
  return false
}

router.beforeEach((to, from, next) => {

  if (to.path.indexOf('/site/') === 0 || to.path.indexOf('/redirectLogin/') === 0) {
    // console.log('SITE')
    localData.theUser = null
    next()
    return
  }

  // console.log('P', from.path, ' => ', to.path)
  
  if (!localData.appMountComplete) {
    next()
    return
  }
  Api.get().ping()
    .then((response) => {

      // make sure that we are enabled for S+T
      if (localData.theUser && localData.theUser.isSmartEnabled === false && !allowedNoSmartPath(to.path)) {
        next({
          path: '/notEnabled/'
        })
        return
      }
      if (localData.theUser && localData.theUser.isSmartEnabled && to.path.indexOf('/notEnabled/') === 0) {
        next({
          path: '/campaigns/'
        })
        return
      }

      // reload for versions
      let localServerVersion = window.localStorage['portal-server-version'] || ''
      // if it's a number
      if (localServerVersion.length < 10) {
        localServerVersion = ''
      }
      if (!localServerVersion) {
        window.localStorage['portal-server-version'] = response.version
      }
      if (moment.utc(response.version).isAfter(moment.utc(localServerVersion))) {
        window.localStorage['portal-server-version'] = response.version
        window.localStorage['portal-version-reload'] = 'TRUE'
        next()
      } else {
        next()
      }
    })
    .catch((e) => {
      localData.theUser = null
      console.log(e)
      // doLogin(to && to.fullPath, e && e.message)
      next({
        path: `/site/login/?next=${encodeURIComponent(router.currentRoute)}`,
        query: {
          next: to && to.fullPath
        }
      })
    })
})

window.app = new Vue({
  router,
  render: h => h(App),
}).$mount('#app')
